import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Row,
  Select,
  Space,
  Spin,
  TimePicker,
  message,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { TimeLimit, WEEKDAY_LABELS } from "./types";
import { getDeliveryConfig, updateDeliveryConfig } from "./api";

interface Props {
  sellerCode: string;
}

interface FormShape {
  weekOffdays: number[];
  timeLimits: { day: number; from: dayjs.Dayjs; to: dayjs.Dayjs; position?: string }[];
}

export const DeliveryConfigCard: React.FC<Props> = ({ sellerCode }) => {
  const [form] = Form.useForm<FormShape>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const data = await getDeliveryConfig(sellerCode);
      form.setFieldsValue({
        weekOffdays: data.weekOffdays ?? [],
        timeLimits: (data.timeLimits ?? []).map((tl) => ({
          day: tl.day,
          from: tl.from ? dayjs(tl.from, "HH:mm") : dayjs("09:00", "HH:mm"),
          to: tl.to ? dayjs(tl.to, "HH:mm") : dayjs("18:00", "HH:mm"),
          position: tl.position,
        })),
      });
    } catch (err: any) {
      message.error("Error cargando delivery config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sellerCode) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellerCode]);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const timeLimits: TimeLimit[] = (values.timeLimits ?? []).map((tl) => ({
        day: tl.day,
        from: tl.from?.format("HH:mm"),
        to: tl.to?.format("HH:mm"),
        position: tl.position,
      }));
      await updateDeliveryConfig(sellerCode, {
        weekOffdays: values.weekOffdays ?? [],
        timeLimits,
      });
      message.success("Delivery config guardado");
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || "Error al guardar";
      message.error(Array.isArray(msg) ? msg.join(" • ") : msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card
      title="Delivery config (vendor)"
      extra={
        <Button type="primary" loading={saving} onClick={handleSave}>
          Guardar
        </Button>
      }
      style={{ marginBottom: 16 }}
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical">
          <Form.Item label="Días sin entrega" name="weekOffdays">
            <Checkbox.Group
              options={WEEKDAY_LABELS.map((label, value) => ({ value, label }))}
            />
          </Form.Item>

          <Form.List name="timeLimits">
            {(fields, { add, remove }) => (
              <div>
                {fields.map((field) => (
                  <Row key={field.key} gutter={8} align="middle" style={{ marginBottom: 8 }}>
                    <Col span={7}>
                      <Form.Item
                        {...field}
                        name={[field.name, "day"]}
                        label={field.name === 0 ? "Día" : ""}
                        rules={[{ required: true }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Select
                          options={WEEKDAY_LABELS.map((label, value) => ({ value, label }))}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={7}>
                      <Form.Item
                        {...field}
                        name={[field.name, "from"]}
                        label={field.name === 0 ? "Desde" : ""}
                        rules={[{ required: true }]}
                        style={{ marginBottom: 0 }}
                      >
                        <TimePicker format="HH:mm" minuteStep={15} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col span={7}>
                      <Form.Item
                        {...field}
                        name={[field.name, "to"]}
                        label={field.name === 0 ? "Hasta" : ""}
                        rules={[{ required: true }]}
                        style={{ marginBottom: 0 }}
                      >
                        <TimePicker format="HH:mm" minuteStep={15} style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>
                    <Col span={3} style={{ display: "flex", alignItems: "center" }}>
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        onClick={() => remove(field.name)}
                        danger
                      />
                    </Col>
                  </Row>
                ))}
                <Button
                  type="dashed"
                  onClick={() =>
                    add({ day: 0, from: dayjs("09:00", "HH:mm"), to: dayjs("18:00", "HH:mm") })
                  }
                  icon={<PlusOutlined />}
                  block
                >
                  Agregar franja horaria
                </Button>
              </div>
            )}
          </Form.List>
        </Form>
      </Spin>
    </Card>
  );
};
